import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  Box,
  Typography,
  Container,
  Paper,
  Button,
  Card,
  CardContent,
  IconButton,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Tooltip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  AccountBalance as CreditsIcon,
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Block as BlockIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { extraCreditsApi, ExtraCredits } from '../../utils/extraCreditsApi';
import AddExtraCreditsModal from '../../components/admin/AddExtraCreditsModal';
import AddCreditsForOrganizationModal from '../../components/admin/AddCreditsForOrganizationModal';
import toast from 'react-hot-toast';

const CreditosPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [extraCredits, setExtraCredits] = useState<ExtraCredits[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [addModalOpen, setAddModalOpen] = useState(false);

  // Verificar se é WhiteLabel baseado na rota ou propriedade do usuário
  const isWhiteLabel = location.pathname.includes('/whitelabel') || user?.isWhiteLabel || user?.role === 'whitelabel';

  const handleBackClick = () => {
    const basePath = isWhiteLabel ? '/admin/whitelabel' : '/admin/super-admin';
    navigate(basePath);
  };

  const loadExtraCredits = async () => {
    try {
      setLoading(true);
      setError(null);
      const credits = await extraCreditsApi.getAllExtraCredits();
      setExtraCredits(credits);
    } catch (err: any) {
      setError(err?.message || 'Erro ao carregar créditos extras');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadExtraCredits();
  }, []);

  const handleDeactivate = async (creditId: string) => {
    if (!window.confirm('Tem certeza que deseja desativar estes créditos extras?')) {
      return;
    }

    try {
      await extraCreditsApi.deactivateExtraCredits(creditId);
      toast.success('Créditos extras desativados com sucesso!');
      await loadExtraCredits();
    } catch (err: any) {
      toast.error(err?.message || 'Erro ao desativar créditos extras');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('pt-BR');
  };

  const formatAmount = (amount: number) => {
    return amount.toLocaleString('pt-BR');
  };

  const totalActiveCredits = extraCredits
    .filter(ec => ec.isActive)
    .reduce((sum, ec) => sum + ec.amount, 0);

  const totalCredits = extraCredits.reduce((sum, ec) => sum + ec.amount, 0);

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between', 
        mb: 3,
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? 2 : 0
      }}>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center',
          width: isMobile ? '100%' : 'auto'
        }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleBackClick}
            sx={{ mr: 2, textTransform: 'none' }}
          >
            Voltar
          </Button>
          <CreditsIcon sx={{ fontSize: 32, mr: 2, color: 'primary.main' }} />
          <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
            {isWhiteLabel ? 'Adicionar Créditos' : 'Créditos Extras'}
          </Typography>
        </Box>

        <Box sx={{ 
          display: 'flex', 
          gap: 1,
          width: isMobile ? '100%' : 'auto',
          justifyContent: isMobile ? 'flex-end' : 'flex-start'
        }}>
          <Tooltip title="Atualizar dados">
            <IconButton onClick={loadExtraCredits} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            sx={{ color: theme.palette.mode === 'dark' ? '#000' : undefined }}
            startIcon={<AddIcon />}
            onClick={() => setAddModalOpen(true)}
          >
            Adicionar Créditos
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Cards de resumo */}
      <Box sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        gap: 3,
        mb: 3
      }}>
        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Total de Créditos Adicionados
            </Typography>
            <Typography variant="h4" color="primary">
              {formatAmount(totalCredits)}
            </Typography>
          </CardContent>
        </Card>

        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Créditos Ativos
            </Typography>
            <Typography variant="h4" color="success.main">
              {formatAmount(totalActiveCredits)}
            </Typography>
          </CardContent>
        </Card>

        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Total de Registros
            </Typography>
            <Typography variant="h4" color="text.primary">
              {extraCredits.length}
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Tabela de créditos extras */}
      <Paper>
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="h6">Histórico de Créditos Extras</Typography>
        </Box>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Organização</TableCell>
                <TableCell align="right">Quantidade</TableCell>
                <TableCell>Motivo</TableCell>
                <TableCell>Adicionado por</TableCell>
                <TableCell>Data de Adição</TableCell>
                <TableCell>Expira em</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {extraCredits.map((credit) => (
                <TableRow key={credit.id}>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {credit.organizationName}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight="bold" color="primary">
                      {formatAmount(credit.amount)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {credit.reason}
                    </Typography>
                  </TableCell>
                  <TableCell>{credit.addedByName}</TableCell>
                  <TableCell>{formatDate(credit.addedAt)}</TableCell>
                  <TableCell>
                    {credit.expiresAt ? formatDate(credit.expiresAt) : 'Não expira'}
                  </TableCell>
                  <TableCell>
                    <Chip
                      sx={{ color: theme.palette.mode === 'dark' ? '#000' : undefined }}
                      label={credit.isActive ? 'Ativo' : 'Inativo'}
                      color={credit.isActive ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    {credit.isActive && (
                      <Tooltip title="Desativar créditos">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeactivate(credit.id)}
                        >
                          <BlockIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {extraCredits.length === 0 && (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">
              Nenhum crédito extra foi adicionado ainda.
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Modal para Super Admin (sem pagamento) */}
      {!isWhiteLabel && (
        <AddExtraCreditsModal
          open={addModalOpen}
          onClose={() => setAddModalOpen(false)}
          onSuccess={loadExtraCredits}
        />
      )}

      {/* Modal para WhiteLabel (com pagamento) */}
      {isWhiteLabel && (
        <AddCreditsForOrganizationModal
          open={addModalOpen}
          onClose={() => setAddModalOpen(false)}
          onSuccess={loadExtraCredits}
        />
      )}
    </Container>
  );
};

export default CreditosPage;
